import axios from 'axios';
import { isByokActive, geminiText } from './geminiByokService';
import { parseScriptFile } from './fileParserService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Get scripts for a project */
export async function getScripts(projectId) {
  const { data } = await axios.get(`${API}/scripts?project_id=${projectId}`);
  return data;
}

/** Create a new script */
export async function createScript(scriptData) {
  const { data } = await axios.post(`${API}/scripts`, scriptData);
  return data;
}

/** Update an existing script — accepts full script data */
export async function updateScript(scriptId, scriptData) {
  const { data } = await axios.put(`${API}/scripts/${scriptId}`, scriptData);
  return data;
}

/**
 * Upload a script file (PDF, DOCX, FDX, Fountain, RTF, Celtx, etc.)
 *
 * For Vault/BYOK users: parses entirely client-side, then saves via createScript/updateScript.
 * For cloud users: uploads to backend for server-side parsing.
 */
export async function uploadScriptFile(file, projectId, existingScriptId = null) {
  if (isByokActive()) {
    return await uploadScriptFileByok(file, projectId, existingScriptId);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_id', projectId);

  const { data } = await axios.post(`${API}/scripts/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data;
}

async function uploadScriptFileByok(file, projectId, existingScriptId) {
  // Parse entirely in the browser — content never leaves the client
  const result = await parseScriptFile(file);
  const text = result.text;

  if (!text || text.trim().length < 10) {
    throw new Error('Could not extract text from file. Try a different format.');
  }

  // Save via normal script create/update (vault users encrypt before sending)
  if (existingScriptId) {
    await updateScript(existingScriptId, {
      project_id: projectId,
      content: text,
      format: 'screenplay',
    });
  } else {
    await createScript({
      project_id: projectId,
      content: text,
      format: 'screenplay',
    });
  }

  return {
    content: text,
    format_detected: result.format,
    page_count: result.pageCount,
    character_count: text.length,
    metadata: result.metadata || {},
  };
}

/** Format script content (AI-powered) */
export async function formatScript(content) {
  if (isByokActive() && content) {
    return await formatScriptByok(content);
  }
  const { data } = await axios.post(`${API}/scripts/format`, { content });
  return data;
}

async function formatScriptByok(content) {
  const systemPrompt = `You are an expert screenplay formatter. Convert raw text into properly formatted screenplay format with scene headings, action, dialogue, and parentheticals.`;
  const userPrompt = `Format this text as a professional screenplay. Return JSON:
{"formatted_content": "the formatted screenplay text", "scenes": [{"number": 1, "heading": "INT. LOCATION - DAY", "content": "scene content"}]}

TEXT:
${content.substring(0, 10000)}

Return ONLY valid JSON.`;

  const text = await geminiText(systemPrompt, userPrompt);
  const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  return JSON.parse(cleaned);
}

/** Re-detect scenes from script content */
export async function redetectScenes(scriptData) {
  if (isByokActive() && scriptData.content) {
    return await redetectScenesByok(scriptData);
  }
  const { data } = await axios.post(`${API}/scripts/redetect-scenes`, scriptData);
  return data;
}

async function redetectScenesByok(scriptData) {
  const systemPrompt = `You are an expert screenplay analyst. Detect and extract scenes from screenplay text.`;
  const userPrompt = `Detect all scenes in this screenplay. Return JSON:
{"scenes": [{"number": 1, "heading": "INT. LOCATION - TIME", "start_line": 0, "content": "scene content"}]}

SCRIPT:
${scriptData.content.substring(0, 10000)}

Return ONLY valid JSON.`;

  const text = await geminiText(systemPrompt, userPrompt);
  const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  return JSON.parse(cleaned);
}
